import { useStore } from '../../lib/hooks/useStore';
import { PaymentCheckServerProps } from '../../lib/typegen/payment-check-server-props';
import style from './style.module.scss';
import { useEffect, useState, useCallback, useRef } from 'react';
import { devices } from '../../lib/devices/devices';
import { DeviceStatusFlags } from '../../lib/devices/MonitoredDevice';
import logger from '../../lib/helpers/logger';
import { CISConfigType } from '../../lib/typegen/cis-config-type';
import { BankAccountType } from '../../lib/typegen/bank-account-type';
import BalancesKvpList from '../balances-kvp-list';
import MessageBoxSet from '../message-box-set';
import checkScannerImage from '../../assets/check-scanner.svg';
import api from '../../lib/api/api';
import { PPConfigType } from '../../lib/typegen/pp-config-type';
import { PayWithCheckRequest } from '../../lib/typegen/pay-with-check-request';
import { LegacyPostAcceptedPayment, LegacyPostRejectedPayment } from '../../lib/types/legacyAdminApi';
import passKeyHelper from '../../lib/helpers/passKey';
import { v4 as guid } from 'uuid';
import { PaymentError } from '../../lib/types/paymentCardState';
import MainMessageBox from '../../features/main-message-box/main-message-box';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import Confirm from '../confirm';
import CheckReaderDevice from '../../lib/devices/check-reader/CheckReaderDevice';
import { PaymentType } from '../../lib/typegen/payment-type';
import enumHelper from '../../lib/helpers/enums';
import { PayWithCheckResponse } from '../../lib/typegen/pay-with-check-response';
import GetCheckView from './components/GetCheckView';
import { formatSavedAccountNumber, resolveSavedBankAccountType } from './savedCheckDetails';
import { useSessionCleanup } from '../../lib/hooks/useSessionCleanup';
import { useCheckInsertSession, type CheckInsertRead } from '../../lib/hooks/useCheckInsertSession';
import { AppEnvironment } from '../../lib/typegen/app-environment';
import KioAppHelper from '../../lib/helpers/kioApp';
import ServiceFeeView from '../service-fee-view';
import { parseAmountToNumber } from '../../lib/helpers/math';
import { PaymentFeeViewServerProps } from '../../lib/typegen/payment-fee-view-server-props';
import GenericButton from '../button-generic';
import CheckInformationImage from '../../assets/check-information.svg';
import { extractCheckNumberFromMicr } from '../../lib/helpers/micr';
import { getApiErrorMessage } from '../../lib/helpers/apiError';
import { CheckPaymentSource, isElectronicCheckSource } from '../../lib/stores/AccountStore';

type PaymentCheckProps = PaymentCheckServerProps;

enum PaymentCheckState {
	OPTIONS,
	AWAITING_INSERT,
	MANUAL_ENTRY,
	RETAIN_INFO,
	PROCESSING,
	ERROR,
	AWAITING_REMOVAL,
	DONE,
	SERVICE_FEE,
}

interface CheckData {
	imagePath: string;
	micr: string;
	account: string;
	routing: string;
	checkNumber?: string;
}

const WAIT_FOR_TAKE_CHECK_MS = 500;
const TAKE_CHECK_REMINDER_MIN_MS = 30000;
const PAYMENT_CHECK_REGISTRY_KEY = 'payment-check';

/** Payment providers whose transaction must be prepared before a check can be paid. */
const requiresPreparedTransaction = (ppType: PPConfigType) => ppType === PPConfigType.GrantStreet || ppType === PPConfigType.InvoiceCloudV1;
const MANUAL_ENTRY_FOCUS_DELAY_MS = 100;
const KEYPAD_REQUEST_DELAY_MS = 200;
const ROUTING_INPUT_NAME = 'check-routing-number';
const ACCOUNT_INPUT_NAME = 'check-account-number';
const ACCOUNT_TYPE_INPUT_NAME = 'account-type';

const customerAlreadyChoseAField = () => {
	const active = document.activeElement;
	return active instanceof HTMLInputElement && (active.name === ACCOUNT_INPUT_NAME || active.name === ACCOUNT_TYPE_INPUT_NAME);
};

// KioWare opens a keyboard from the input's type the moment the field focuses, and it
// ignores inputMode. These stay type='text' because the masked values contain '*', so that
// automatic keyboard is QWERTY. Asking for the numpad in the same moment loses. Wait, then
// ask, the same way the payment amount field does. One pending request: a newer focus or
// leaving the screen cancels it, and it opens only if that field is still focused.
let keypadRequestTimeout: ReturnType<typeof setTimeout> | undefined;

const cancelNumericKeypadRequest = () => {
	clearTimeout(keypadRequestTimeout);
	keypadRequestTimeout = undefined;
};

const requestNumericKeypad = (inputName: string) => {
	cancelNumericKeypadRequest();
	keypadRequestTimeout = setTimeout(() => {
		keypadRequestTimeout = undefined;
		const active = document.activeElement;
		if (!(active instanceof HTMLInputElement) || active.name !== inputName) return;

		KioAppHelper('StartKeyboard', '', '', 'number', false, '', inputName);
	}, KEYPAD_REQUEST_DELAY_MS);
};

const PaymentCheckWithManualEntry = function ({
	nextPageTarget,
	previousPageTarget,
	stampVoid,
	imageAltKey,
	paymentTotalLabelKey,
	infoBoxLabelKey,
	warningBoxLabelKey,
	errorLabel,
	paymentFeeViewServerProps,
	enablePreReceiptCheckRemovalPrompt,
	enableCheckInsertSession,
}: PaymentCheckProps) {
	const {
		accountStore: {
			cartItems,
			setPaymentType,
			cartTotalAmount,
			transactionFee,
			setTransactionFee,
			cartId,
			setCartId,
			token,
			setToken,
			hasPreparedTransaction,
			setHasPreparedTransaction,
			payorInformation,
			savedCheckDetails,
			setIsSavingCheckInfo,
			setPreReceiptCheckRemovalPromptHandled,
			setCheckPaymentSource,
		},
		sessionStore: { legacyId, unregisterCleanup, setSkipCountdownModal, setTimeoutNavigationCallback, beginKioWareSession },
		kioskStore: { kioskId, companyId },
		uiStore: { handleNavigation, setBackEnabled, setCanCancel, showLoader, clearCustomBackAction, setCustomBackAction },
		configStore: { getString, envDeviceOption, cisType, ppType, kioskConfigEnvironment, kioskLocation, config },
	} = useStore();

	const [paymentState, setPaymentState] = useState(
		(ppType === PPConfigType.GrantStreet || ppType === PPConfigType.InvoiceCloudV1) && transactionFee
			? PaymentCheckState.SERVICE_FEE
			: PaymentCheckState.OPTIONS
	);
	const [paymentInputSource, setPaymentInputSource] = useState<CheckPaymentSource>('scanner');
	const [retainInfoSource, setRetainInfoSource] = useState<'manual' | 'saved'>('manual');
	const [error, setError] = useState<PaymentError | null>(null);
	const [checkReader, setCheckReader] = useState<CheckReaderDevice | null>(null);
	const [isCleanupRequired, setIsCleanupRequired] = useState<boolean>(false);
	const [checkData, setCheckData] = useState<CheckData | null>(null);
	// Set when preparing the transaction fails; only acted on when the check insert session is on.
	const [prepareFailure, setPrepareFailure] = useState<{ error: unknown } | null>(null);
	const [manualRouting, setManualRouting] = useState('');
	const [manualAccount, setManualAccount] = useState('');
	const [manualAccountType, setManualAccountType] = useState<BankAccountType>(BankAccountType.Checking);
	// Display-only: never read by handleManualPaymentConfirm, so the saved path keeps
	// posting empty accountNumber/routingNumber exactly as CC&B already accepts.
	const [savedAccountDisplay, setSavedAccountDisplay] = useState('');

	const isPreparingTransaction = useRef(false);
	const hasPreparedTransactionRef = useRef(hasPreparedTransaction);
	hasPreparedTransactionRef.current = hasPreparedTransaction;
	const previousViewBeforeProcessingRef = useRef(PaymentCheckState.OPTIONS);

	const [isPrepared, setIsPrepared] = useState(false);
	const isPreparedRef = useRef(false);
	isPreparedRef.current = isPrepared;
	const isPayGoCustomer = cisType === CISConfigType.PayGo || ppType === PPConfigType.PayGo;
	const isMyUsageCustomer = cisType === CISConfigType.MyUsage || ppType === PPConfigType.MyUsage;
	const supportsPrePayFlag = isPayGoCustomer || isMyUsageCustomer;
	const takeCheckReminderStartedAtRef = useRef<number | null>(null);
	const routingInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setPaymentType(PaymentType.Check);
		setIsSavingCheckInfo(false);
		setPreReceiptCheckRemovalPromptHandled(false);
		setCheckPaymentSource(undefined);
	}, [setPaymentType, setIsSavingCheckInfo, setPreReceiptCheckRemovalPromptHandled, setCheckPaymentSource]);

	useEffect(() => {
		if (paymentState === PaymentCheckState.AWAITING_REMOVAL) {
			takeCheckReminderStartedAtRef.current = Date.now();
			setSkipCountdownModal(true);
			setTimeoutNavigationCallback(null);
			beginKioWareSession();
			const autoAdvanceHandle = setTimeout(() => {
				setPaymentState(PaymentCheckState.DONE);
			}, TAKE_CHECK_REMINDER_MIN_MS);
			return () => {
				clearTimeout(autoAdvanceHandle);
				takeCheckReminderStartedAtRef.current = null;
				setSkipCountdownModal(false);
				setTimeoutNavigationCallback(null);
			};
		}
	}, [paymentState, setSkipCountdownModal, setTimeoutNavigationCallback, beginKioWareSession]);

	const prepareTransaction = useCallback(async () => {
		logger.logGeneral.log(
			{},
			`[prepareTransaction] called — ppType: ${ppType}, hasPreparedTransaction: ${hasPreparedTransaction}, isPreparingTransaction.current: ${isPreparingTransaction.current}`
		);
		const requiresPrepare = requiresPreparedTransaction(ppType);
		if (!requiresPrepare) {
			logger.logGeneral.log({}, 'Prepare transaction not required for this PP config, skipping...');
			return;
		}

		if (isPreparingTransaction.current || hasPreparedTransaction) {
			logger.logGeneral.log({}, 'Transaction already prepared or in-flight, skipping...');
			return;
		}

		logger.logGeneral.log({}, '[prepareTransaction] proceeding with API call');
		isPreparingTransaction.current = true;

		const prepareTransactionRequest = (() => {
			switch (ppType) {
				case PPConfigType.GrantStreet:
					return {
						accountsToPay: cartItems.map((item) => ({
							accountNumber: item.item.itemNumber,
							amount: item.amountToPay,
						})),
						paymentType: 'ECHECK',
						location: kioskLocation,
						totalAmount: cartTotalAmount,
						payorInfo: payorInformation,
					};
				case PPConfigType.InvoiceCloudV1:
					return {
						accountsToPay: cartItems.map((item) => ({
							accountNumber: item.item.itemNumber,
							amount: item.amountToPay,
							invoiceGuid: item.item.itemGuid,
							icAchPaymentId: item.item.icAchPaymentId,
						})),
						paymentType: 'ECHECK',
						location: kioskLocation,
						totalAmount: cartTotalAmount,
						payorInfo: payorInformation,
					};
			}
		})();

		if (!prepareTransactionRequest) {
			logger.logGeneral.error({}, 'No prepare transaction request could be constructed', 'No prepare transaction request could be constructed');
			throw new Error('No prepare transaction request could be constructed');
		}

		try {
			logger.logGeneral.log({}, '[prepareTransaction] calling api.payments.prepareTransaction...');
			const data = await api.payments.prepareTransaction(prepareTransactionRequest);
			const fees = data?.fees?.eCheck?.fees;

			if (data?.cartId) {
				logger.logGeneral.log({}, `[prepareTransaction] setting cartId: ${data.cartId}`);
				setCartId(data.cartId);
				setToken(data.token!);
			}

			if (fees) {
				logger.logGeneral.log({}, `[prepareTransaction] fees found, setting SERVICE_FEE state. fees: ${fees}`);
				setTransactionFee(fees);
				setPaymentState(PaymentCheckState.SERVICE_FEE);
			} else {
				logger.logGeneral.log({}, '[prepareTransaction] no fees in response');
			}

			logger.logGeneral.log({}, '[prepareTransaction] setting hasPreparedTransaction = true');
			setHasPreparedTransaction(true);
			setIsPrepared(true);
		} catch (error) {
			logger.logGeneral.error({}, '[prepareTransaction] ERROR:', error);
			logger.logGeneral.error({}, 'Failed to prepare transaction:', error);
			setPrepareFailure({ error });
			isPreparingTransaction.current = false;
		}
	}, [
		ppType,
		hasPreparedTransaction,
		setHasPreparedTransaction,
		cartItems,
		kioskLocation,
		cartTotalAmount,
		payorInformation,
		setCartId,
		setToken,
		setTransactionFee,
	]);

	useEffect(() => {
		if (ppType === PPConfigType.GrantStreet || ppType === PPConfigType.InvoiceCloudV1) prepareTransaction();
	}, [ppType, prepareTransaction]);

	useEffect(() => {
		if (paymentState === PaymentCheckState.OPTIONS) {
			clearCustomBackAction();
			return;
		}

		const handleGoBack = () => {
			switch (paymentState) {
				case PaymentCheckState.MANUAL_ENTRY:
				case PaymentCheckState.AWAITING_INSERT:
					setError(null);
					setPaymentState(PaymentCheckState.OPTIONS);
					break;
				case PaymentCheckState.RETAIN_INFO:
					setError(null);
					if (retainInfoSource === 'saved') {
						setPaymentState(PaymentCheckState.OPTIONS);
						setPaymentInputSource('scanner');
					} else {
						setPaymentState(PaymentCheckState.MANUAL_ENTRY);
					}
					break;
				case PaymentCheckState.ERROR:
					setError(null);
					setPaymentState(previousViewBeforeProcessingRef.current);
					break;
				default:
					clearCustomBackAction();
					break;
			}
		};

		setCustomBackAction(handleGoBack);

		return () => clearCustomBackAction();
	}, [paymentState, setCustomBackAction, clearCustomBackAction, retainInfoSource]);

	const handleCheckPayment = useCallback(
		async (sourceCheckData: CheckData, source: CheckPaymentSource = 'scanner') => {
			logger.logGeneral.log(
				{},
				`[handleCheckPayment] called — source: ${source}, ppType: ${ppType}, hasPreparedTransactionRef: ${hasPreparedTransactionRef.current}`
			);

			const requiresPrepare = requiresPreparedTransaction(ppType);
			if (requiresPrepare && !hasPreparedTransactionRef.current) {
				logger.logGeneral.log(
					{},
					`[handleCheckPayment] BAILING — requiresPrepare: ${requiresPrepare}, hasPreparedTransactionRef: ${hasPreparedTransactionRef.current}`
				);
				return;
			}

			if (source === 'scanner' && paymentState !== PaymentCheckState.AWAITING_INSERT) {
				logger.logGeneral.log({}, 'Invalid state for scanner check payment', 'Invalid state for scanner check payment');
				return;
			}

			const updateCheckData = (
				checkData: CheckData,
				accountNumber: string | null,
				routingNumber: string | null,
				checkNumber: string | null
			) => {
				const updatedCheckData = { ...checkData };
				if (accountNumber) updatedCheckData.account = accountNumber;
				if (routingNumber) updatedCheckData.routing = routingNumber;
				if (checkNumber) updatedCheckData.checkNumber = checkNumber;
				return updatedCheckData;
			};

			const handleTestCheckData = (data: CheckData): CheckData => {
				try {
					switch (kioskConfigEnvironment) {
						case AppEnvironment.Dev:
						case AppEnvironment.QA:
						case AppEnvironment.Demo: {
							const accountNumber =
								KioAppHelper('DoUrlSubstitution', '$$BPK_TEST_CHK_ACC_NUM$$') || import.meta.env.VITE_TEST_ACC_NUMBER;
							const routingNumber =
								KioAppHelper('DoUrlSubstitution', '$$BPK_TEST_CHK_RTN_NUM$$') || import.meta.env.VITE_TEST_ROUTING_NUMBER;
							const checkNumber = Math.floor(Math.random() * 9999)
								.toString()
								.padStart(4, '0');
							return updateCheckData(data, accountNumber, routingNumber, checkNumber);
						}
						default:
							return data;
					}
				} catch (error) {
					logger.logGeneral.error({}, 'Error applying test check number substitutions', error);
					return data;
				}
			};

			const resolvedCheckData = source === 'scanner' ? handleTestCheckData(sourceCheckData) : sourceCheckData;

			if (!resolvedCheckData) {
				logger.logGeneral.log({}, 'No check data available for processing payment', 'No check data available for processing payment');
				return;
			}

			const item = cartItems[0];
			if (!item.customer) item.customer = { customerId: cartId || '', name: `${payorInformation?.firstName} ${payorInformation?.lastName}` };

			let response: PayWithCheckResponse | null = null;
			try {
				previousViewBeforeProcessingRef.current = paymentState;
				setCanCancel(false);
				showLoader();
				setPaymentState(PaymentCheckState.PROCESSING);

				const identifier = cartId ? cartId : item.id!;
				const utilityAccountNumber =
					ppType === PPConfigType.MyUsage || ppType === PPConfigType.PayGo
						? item.item.itemNumber
						: identifier;
				const resolvedCheckNumber =
					resolvedCheckData.checkNumber?.trim() || extractCheckNumberFromMicr(resolvedCheckData.micr) || '';
				const request: PayWithCheckRequest = {
					utilityAccountNumber,
					accountToken: item.item.accountToken,
					amountToPay: cartTotalAmount!,
					amountDue: cartTotalAmount!,
					isPrePay: supportsPrePayFlag ? item.item.isPrePay : undefined,
					serviceFee: transactionFee ? parseAmountToNumber(transactionFee) : 0,
					customerName: item!.customer!.name!,
					routingNumber: resolvedCheckData.routing,
					accountNumber: resolvedCheckData.account,
					checkNumber: resolvedCheckNumber,
					accountType: manualAccountType,
					micrData: resolvedCheckData.micr,
					// Local scanner file paths are not base64; omit until image upload is implemented.
					checkImage: undefined,
					terminalCity: kioskLocation == 'kiosk_mis' ? 'MISS' : 'KATY',
					terminalState: 'TX',
					zip: item.item.zip,
					auxiliaryOnUs: resolvedCheckData.micr.includes('*'),
					cartId: cartId!,
					token: token!,
					invoiceGuid: item.item.itemGuid,
					paymentTypeId: item.item.icAchPaymentId,
					group: item.item.group ?? undefined,
					classification: item.item.classification ?? undefined,
				};

				if (ppType === PPConfigType.DynaTouch) request.invoiceGuid = guid();

				const legacyPostAcceptedPayment: LegacyPostAcceptedPayment = structuredClone({
					dynatouchGUID: legacyId!,
					firstCashTransaction: false,
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					paymentGatewayExternalID: item!.item.itemNumber,
					paymentDateTime: new Date().toISOString(),
					paymentAmount: cartTotalAmount! + (transactionFee ? parseAmountToNumber(transactionFee) : 0),
					paymentType: 'Check',
					paymentGUID: guid(),
					passKey: await passKeyHelper.generatePassKey(),
					cashOnes: 0,
					cashTwos: 0,
					cashFives: 0,
					cashTens: 0,
					cashTwenties: 0,
					cashFifties: 0,
					cashHundreds: 0,
					cisProvider: enumHelper.getCisConfigTypeLabel(cisType),
					paymentGateway: enumHelper.getPpConfigTypeLabel(ppType),
					cisProviderExternalID: item!.id!,
					payorName: item!.customer!.name!,
				});

				const legacyPostRejectedPayment: LegacyPostRejectedPayment = structuredClone({
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					billpaySessionGUID: legacyId!,
					rejectedECID: '',
					rejectedReason: 'Check payment error',
					rejectedPaymentType: 'Check',
					paymentAmount: cartTotalAmount! + (transactionFee ? parseAmountToNumber(transactionFee) : 0),
					payorName: item!.customer!.name!,
					paymentDateTime: new Date().toISOString(),
					passKey: await passKeyHelper.generatePassKey(),
				});

				setBackEnabled(false);
				response = await api.payments.check(request, legacyPostAcceptedPayment, legacyPostRejectedPayment);
				if (!response.paymentId) throw new Error('No payment ID returned from check payment API');

				logger.logCheckPayment.log(
					{
						paymentId: response.paymentId,
						customerName: item!.customer!.name!,
						cISConfig: cisType,
						pPConfig: ppType,
						referenceNumberLabel: response.referenceNumberLabel,
						referenceNumberValue: response.referenceNumberValue,
						amountToPay: item!.amountToPayWithFee!,
						checkAmount: item!.amountToPayWithFee!,
					},
					'Check payment successful'
				);

				setCheckPaymentSource(source);

				// No physical check went into the acceptor for manual or saved details, so there is nothing to take back.
				if (isElectronicCheckSource(source)) {
					setPaymentState(PaymentCheckState.DONE);
				} else if (enablePreReceiptCheckRemovalPrompt) {
					setPreReceiptCheckRemovalPromptHandled(true);
					setPaymentState(PaymentCheckState.AWAITING_REMOVAL);
				} else {
					setPaymentState(PaymentCheckState.DONE);
				}
			} catch (error) {
				setError({ message: getApiErrorMessage(error, getString('check-payment-error')) });
				setPaymentState(PaymentCheckState.ERROR);
				logger.logCheckPayment.error(
					{
						paymentId: response?.paymentId ?? '',
						customerName: item?.customer?.name ?? '',
						cISConfig: cisType,
						pPConfig: ppType,
						referenceNumberLabel: response?.referenceNumberLabel ?? '',
						referenceNumberValue: response?.referenceNumberValue ?? '',
						amountToPay: item?.amountToPayWithFee ?? 0,
						checkAmount: item?.amountToPayWithFee ?? 0,
					},
					'Error processing check payment:',
					error
				);
			} finally {
				setIsCleanupRequired(false);
				unregisterCleanup(PAYMENT_CHECK_REGISTRY_KEY);
				setCanCancel(true);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			cartItems,
			legacyId,
			paymentState,
			checkReader,
			enablePreReceiptCheckRemovalPrompt,
			setPreReceiptCheckRemovalPromptHandled,
			setCheckPaymentSource,
			setSkipCountdownModal,
			setTimeoutNavigationCallback,
		]
	);

	useSessionCleanup(
		PAYMENT_CHECK_REGISTRY_KEY,
		useCallback(async () => {
			if (!isCleanupRequired) return;
			logger.logGeneral.warn({}, 'Cleaning up incomplete check payment session');

			try {
				if (devices.checkReader) devices.checkReader.cancelRead();
				if (devices.checkReaderStub) devices.checkReaderStub.cancelRead();
			} catch (error) {
				logger.logGeneral.error({}, 'Error cancelling check read during cleanup', error);
			}
		}, [isCleanupRequired])
	);

	useEffect(() => {
		const checkReaderOk =
			devices.checkReader.getStatus().statusBits === DeviceStatusFlags.Ok && (config.ignoreCheckSensor || !devices.checkReader.checkInserted());
		logger.logGeneral.log({}, 'Check reader status in payment-check ok:', checkReaderOk);
		if (config.ignoreCheckSensor) {
			logger.logGeneral.log({}, 'Ignoring check sensor - forcing real check reader device');
		}

		switch (envDeviceOption) {
			case 'always':
				setCheckReader(devices.checkReader);
				break;
			case 'never':
				setCheckReader(devices.checkReaderStub);
				break;
			case 'with-fallback':
				if (checkReaderOk) {
					setCheckReader(devices.checkReader);
					logger.logGeneral.log({}, 'Using check reader device for check payments');
				} else {
					setCheckReader(devices.checkReaderStub);
					logger.logGeneral.log({}, 'Check reader device not available, using stub for check payments');
				}
				break;
		}
	}, [envDeviceOption, config.ignoreCheckSensor]);

	useEffect(() => {
		return () => {
			checkReader?.cancelRead();
		};
	}, [checkReader]);

	// ConEd: on unless the config turns it off (#8844).
	const checkInsertSessionEnabled = enableCheckInsertSession ?? true;

	const handleScannerCheckRead = (read: CheckInsertRead) => {
		// handleCheckPayment drops a read that arrives before prepare finishes; keep the scanner armed instead.
		if (requiresPreparedTransaction(ppType) && !hasPreparedTransactionRef.current) return false;
		setError(null);
		setCheckData(read);
		setIsCleanupRequired(true);
		handleCheckPayment(read, 'scanner');
		return true;
	};

	const handleScannerDeviceError = (err: number) => {
		logger.logGeneral.error({}, 'Check reading error', 'Check reading error');
		logger.logCheckPayment.error(
			{
				paymentId: cartId ?? '',
				customerName: `${payorInformation?.firstName || 'No first name'} ${payorInformation?.lastName || 'No last name'}`,
				cISConfig: CISConfigType.None,
				pPConfig: PPConfigType.None,
				referenceNumberLabel: '',
				referenceNumberValue: '',
				amountToPay: 0,
				checkAmount: 0,
			},
			`Error reading check: ${err}`,
			err.toString()
		);
		setError({ message: getString('check-reading-error') });
		setPaymentState(PaymentCheckState.ERROR);
	};

	useCheckInsertSession({
		// Arm only once a check can actually be paid; a read before prepare finishes would be thrown away.
		// The screen is not an observer: isPrepared is the state that re-renders it when prepare succeeds, and
		// hasPreparedTransaction covers a payment prepared on an earlier visit, where prepare returns early.
		enabled:
			checkInsertSessionEnabled &&
			paymentInputSource === 'scanner' &&
			paymentState === PaymentCheckState.AWAITING_INSERT &&
			(!requiresPreparedTransaction(ppType) || isPrepared || hasPreparedTransaction),
		checkReader,
		stampVoid: !!stampVoid,
		ignoreCheckSensor: !!config.ignoreCheckSensor,
		onCheckRead: handleScannerCheckRead,
		onDeviceError: handleScannerDeviceError,
	});

	// The scanner only arms after prepare succeeds, so a failed prepare would leave an insert screen that can
	// never read a check. Show the error instead; Try again prepares again.
	useEffect(() => {
		if (!checkInsertSessionEnabled || !prepareFailure || paymentInputSource !== 'scanner' || paymentState !== PaymentCheckState.AWAITING_INSERT)
			return;
		setError({ message: getApiErrorMessage(prepareFailure.error, getString('check-payment-error')) });
		setPaymentState(PaymentCheckState.ERROR);
	}, [checkInsertSessionEnabled, prepareFailure, paymentInputSource, paymentState, getString]);

	useEffect(() => {
		if (!checkReader) {
			logger.logGeneral.log({}, '[checkReaderEffect] no checkReader, returning early');
			return;
		}

		if (paymentInputSource !== 'scanner') return;

		let waitForTakeCheckInterval = 0;
		let retryTimeout: ReturnType<typeof setTimeout> | null = null;
		let aborted = false;

		switch (paymentState) {
			case PaymentCheckState.AWAITING_INSERT: {
				logger.logGeneral.log({}, '[checkReaderEffect] AWAITING_INSERT — setting up scanner');
				setBackEnabled(true);

				if (checkInsertSessionEnabled) {
					logger.logGeneral.log({}, '[checkReaderEffect] check insert session enabled, useCheckInsertSession drives the scanner');
					break;
				}

				if (config.ignoreCheckSensor) {
					const initiateRead = () => {
						if (aborted) {
							logger.logGeneral.log({}, '[initiateRead] aborted, returning');
							return;
						}

						logger.logGeneral.log({}, '[initiateRead] calling checkReader.readCheck...');
						checkReader.readCheck(!!stampVoid, (err, imagePath, micr, account, routing) => {
							logger.logGeneral.log({}, `[initiateRead] readCheck callback — err: ${err}, aborted: ${aborted}`);
							if (aborted) return;

							if (err === DeviceStatusFlags.Ok) {
								setError(null);
								setCheckData({ imagePath, micr, account, routing });
								setIsCleanupRequired(true);
								handleCheckPayment({ imagePath, micr, account, routing }, 'scanner');
							} else {
								logger.logGeneral.error(
									{},
									'Check reading error (ignoreCheckSensor mode)',
									'Check reading error (ignoreCheckSensor mode)'
								);
								logger.logCheckPayment.error(
									{
										paymentId: cartId ?? '',
										customerName: `${payorInformation?.firstName || 'No first name'} ${payorInformation?.lastName || 'No last name'}`,
										cISConfig: CISConfigType.None,
										pPConfig: PPConfigType.None,
										referenceNumberLabel: '',
										referenceNumberValue: '',
										amountToPay: 0,
										checkAmount: 0,
									},
									`Error reading check: ${err}`,
									err.toString()
								);

								if (!aborted) {
									logger.logGeneral.log({}, 'ignoreCheckSensor is true - retrying read after failed attempt');
									checkReader.cancelRead();
									retryTimeout = setTimeout(() => {
										initiateRead();
									}, 1000);
								}
							}
						});
					};

					logger.logGeneral.log({}, '[checkReaderEffect] ignoreCheckSensor — cancelling read and scheduling initiateRead in 500ms');
					checkReader.cancelRead();
					retryTimeout = setTimeout(() => {
						initiateRead();
					}, 500);
				} else {
					checkReader.readCheck(!!stampVoid, (err, imagePath, micr, account, routing) => {
						if (err === DeviceStatusFlags.Ok) {
							setError(null);
							setCheckData({ imagePath, micr, account, routing });
							setIsCleanupRequired(true);
							handleCheckPayment({ imagePath, micr, account, routing }, 'scanner');
						} else {
							logger.logGeneral.error({}, 'Check reading error', 'Check reading error');
							logger.logCheckPayment.error(
								{
									paymentId: cartId ?? '',
									customerName: `${payorInformation?.firstName || 'No first name'} ${payorInformation?.lastName || 'No last name'}`,
									cISConfig: CISConfigType.None,
									pPConfig: PPConfigType.None,
									referenceNumberLabel: '',
									referenceNumberValue: '',
									amountToPay: 0,
									checkAmount: 0,
								},
								`Error reading check: ${err}`,
								err.toString()
							);
						}
					});
				}
				break;
			}
			case PaymentCheckState.AWAITING_REMOVAL:
				waitForTakeCheckInterval = setInterval(() => {
					if (!checkReader?.checkInserted()) {
						const startedAt = takeCheckReminderStartedAtRef.current;
						if (startedAt && Date.now() - startedAt >= TAKE_CHECK_REMINDER_MIN_MS) {
							clearInterval(waitForTakeCheckInterval);
							waitForTakeCheckInterval = 0;
							setPaymentState(PaymentCheckState.DONE);
						}
					}
				}, WAIT_FOR_TAKE_CHECK_MS);
				break;
			case PaymentCheckState.SERVICE_FEE:
				setPaymentState(PaymentCheckState.SERVICE_FEE);
				break;
			case PaymentCheckState.DONE:
				handleNavigation(nextPageTarget);
				break;
			default:
				break;
		}

		return () => {
			aborted = true;
			if (waitForTakeCheckInterval) clearInterval(waitForTakeCheckInterval);
			if (retryTimeout) clearTimeout(retryTimeout);
		};
	}, [
		cartId,
		checkData,
		checkReader,
		handleCheckPayment,
		handleNavigation,
		nextPageTarget,
		paymentState,
		paymentInputSource,
		payorInformation?.firstName,
		payorInformation?.lastName,
		stampVoid,
		hasPreparedTransaction,
		setBackEnabled,
		config.ignoreCheckSensor,
		checkInsertSessionEnabled,
	]);

	useEffect(() => {
		if (paymentState === PaymentCheckState.DONE) handleNavigation(nextPageTarget);
	}, [paymentState, handleNavigation, nextPageTarget]);

	// Every entry into manual entry (first open, Deny, Back from RETAIN_INFO) focuses the routing
	// input, unless the customer already moved to the account field or an account-type choice.
	// Its onFocus asks KioWare for the numeric keypad after the letter keyboard has opened.
	useEffect(() => {
		if (paymentState !== PaymentCheckState.MANUAL_ENTRY) return;

		const focusTimeout = setTimeout(() => {
			if (customerAlreadyChoseAField()) return;
			routingInputRef.current?.focus();
		}, MANUAL_ENTRY_FOCUS_DELAY_MS);
		return () => {
			clearTimeout(focusTimeout);
			cancelNumericKeypadRequest();
		};
	}, [paymentState]);

	const handleRetry = () => {
		setError(null);
		if (checkInsertSessionEnabled && prepareFailure) {
			setPrepareFailure(null);
			prepareTransaction();
		}
		if (paymentInputSource === 'scanner') setPaymentState(PaymentCheckState.AWAITING_INSERT);
		else setPaymentState(PaymentCheckState.OPTIONS);
	};

	const handleCancel = () => {
		setError(null);
		setPaymentState(PaymentCheckState.OPTIONS);
		handleNavigation(previousPageTarget);
	};

	const handleServiceFeeConfirm = () => {
		if (config.ignoreCheckSensor && checkReader) {
			checkReader.cancelRead();
		}
		setPaymentState(PaymentCheckState.OPTIONS);
	};

	const handleServiceFeeCancel = () => {
		handleNavigation(previousPageTarget);
	};

	const maskValue = (value: string, visibleDigits: number) => {
		if (!value) return '';
		if (value.length <= visibleDigits) return value;
		return `${'*'.repeat(value.length - visibleDigits)}${value.slice(-visibleDigits)}`;
	};

	const extractDigits = (value: string) => value.replace(/\D/g, '');

	const resolveMaskedInput = (inputValue: string, previousRawValue: string, visibleDigits: number) => {
		const digitsOnlyInput = extractDigits(inputValue);
		const previousMaskedValue = maskValue(previousRawValue, visibleDigits);

		if (!inputValue.includes('*')) {
			return digitsOnlyInput;
		}

		if (inputValue.length < previousMaskedValue.length) {
			const removedCount = previousMaskedValue.length - inputValue.length;
			return previousRawValue.slice(0, Math.max(0, previousRawValue.length - removedCount));
		}

		if (inputValue.startsWith(previousMaskedValue)) {
			const appendedPart = extractDigits(inputValue.slice(previousMaskedValue.length));
			return `${previousRawValue}${appendedPart}`;
		}

		const preservedPrefixLength = Math.max(0, previousRawValue.length - visibleDigits);
		const preservedPrefix = previousRawValue.slice(0, preservedPrefixLength);
		return `${preservedPrefix}${digitsOnlyInput}`;
	};

	const handleRoutingChange = (value: string) => {
		setManualRouting((previous) => resolveMaskedInput(value, previous, 3));
	};

	const handleAccountChange = (value: string) => {
		setManualAccount((previous) => resolveMaskedInput(value, previous, 4));
	};

	const handleUseScanner = () => {
		setPaymentInputSource('scanner');
		setError(null);
		setPaymentState(PaymentCheckState.AWAITING_INSERT);
	};

	const handleEnterCheckDetails = () => {
		setPaymentInputSource('manual');
		setSavedAccountDisplay('');
		setError(null);
		setPaymentState(PaymentCheckState.MANUAL_ENTRY);
	};

	const handleUseSavedCheckDetails = () => {
		setPaymentInputSource('saved');
		setError(null);
		setManualAccountType(resolveSavedBankAccountType(savedCheckDetails.savedACHLastAccountType));
		setSavedAccountDisplay(formatSavedAccountNumber(savedCheckDetails.savedACHLastFour));
		setManualAccount('');
		setManualRouting('');
		setRetainInfoSource('saved');
		setPaymentState(PaymentCheckState.RETAIN_INFO);
	};

	const handleManualDone = () => {
		setRetainInfoSource('manual');
		setPaymentState(PaymentCheckState.RETAIN_INFO);
	};

	const handleConfirmationDeny = () => {
		if (retainInfoSource === 'saved') {
			setPaymentState(PaymentCheckState.OPTIONS);
			setPaymentInputSource('scanner');
		} else {
			setPaymentState(PaymentCheckState.MANUAL_ENTRY);
		}
	};

	const handleManualPaymentConfirm = () => {
		let preparedManualCheckData: CheckData;
		let source: CheckPaymentSource = 'manual';

		if (paymentInputSource === 'saved') {
			preparedManualCheckData = {
				imagePath: '',
				micr: '',
				account: '',
				routing: '',
			};
			source = 'saved';
		} else {
			preparedManualCheckData = {
				imagePath: '',
				micr: '',
				account: manualAccount,
				routing: manualRouting,
			};
			source = 'manual';
		}

		setIsSavingCheckInfo(paymentInputSource !== 'saved');
		handleCheckPayment(preparedManualCheckData, source);
	};

	const renderPayView = () => (
		<section className={style.section}>
			<img className={style.section__image} src={checkScannerImage} alt={imageAltKey} />
			<BalancesKvpList
				className={style.section__details}
				kvpList={[]}
				totalSum={{
					label: getString(paymentTotalLabelKey ?? ''),
					value: (cartTotalAmount ?? 0) + (transactionFee ? parseAmountToNumber(transactionFee) : 0),
					forceRender: true,
				}}
			/>
			<MessageBoxSet
				className={style.section__messages}
				infoText={error ? `${getString(errorLabel ?? '')}.\n${getString(infoBoxLabelKey ?? '')}` : getString(infoBoxLabelKey ?? '')}
				warningText={getString(warningBoxLabelKey ?? '')}
			/>
		</section>
	);

	const renderOptionsView = () => (
		<section className={style.manual}>
			<div className={style.manual__actions}>
				<GenericButton className={style.manual__optionButton} innerText={getString('use-check-scanner')} onClick={handleUseScanner} />
				<GenericButton
					className={style.manual__optionButton}
					innerText={getString('enter-check-details')}
					onClick={handleEnterCheckDetails}
				/>
				{savedCheckDetails && savedCheckDetails.savedACHLastFour && savedCheckDetails.savedACHLastFour.length > 0 && (
					<GenericButton
						className={style.manual__optionButton}
						innerText={getString('use-saved-check-details')}
						onClick={handleUseSavedCheckDetails}
					/>
				)}
			</div>
		</section>
	);

	const renderManualEntryView = () => (
		<section className={style.manual}>
			<img className={style.manual__image} src={CheckInformationImage} alt={imageAltKey} />
			<div className={style.manual__radioGroup}>
				<label className={style.manual__radioLabel}>
					<input
						type='radio'
						name={ACCOUNT_TYPE_INPUT_NAME}
						checked={manualAccountType === BankAccountType.Checking}
						onChange={() => setManualAccountType(BankAccountType.Checking)}
					/>
					<span>{getString('checking-account-option')}</span>
				</label>
				<label className={style.manual__radioLabel}>
					<input
						type='radio'
						name={ACCOUNT_TYPE_INPUT_NAME}
						checked={manualAccountType === BankAccountType.Savings}
						onChange={() => setManualAccountType(BankAccountType.Savings)}
					/>
					<span>{getString('savings-account-option')}</span>
				</label>
			</div>
			<div className={style.manual__fields}>
				<input
					ref={routingInputRef}
					type='text'
					name={ROUTING_INPUT_NAME}
					placeholder={getString('routing-number-placeholder')}
					dir='ltr'
					value={maskValue(manualRouting, 3)}
					onChange={(event) => handleRoutingChange(event.target.value)}
					onFocus={() => requestNumericKeypad(ROUTING_INPUT_NAME)}
					className={style.manual__input}
				/>
				<input
					type='text'
					name={ACCOUNT_INPUT_NAME}
					placeholder={getString('bank-account-number-placeholder')}
					dir='ltr'
					value={maskValue(manualAccount, 4)}
					onChange={(event) => handleAccountChange(event.target.value)}
					onFocus={() => requestNumericKeypad(ACCOUNT_INPUT_NAME)}
					className={style.manual__input}
				/>
			</div>
			<GenericButton
				icon={AppIconType.AcceptSolid}
				innerText={getString('done')}
				hasGlow={true}
				isGrowHorizontally={false}
				disabled={!manualRouting.trim() || !manualAccount.trim()}
				onClick={handleManualDone}
			/>
		</section>
	);

	const renderRetainInfoView = () => (
		<section className={style.manual}>
			<div className={style.manual__summary}>
				<ul className={style.manual__summaryList}>
					<li className={style.manual__summaryItem}>
						<span className={style.manual__summaryLabel}>{getString('account-type-label')}:</span>
						<span className={style.manual__summaryValue}>
							{manualAccountType === BankAccountType.Checking ? getString('checking-account-type') : getString('savings-account-type')}
						</span>
					</li>
					{manualRouting && (
						<li className={style.manual__summaryItem}>
							<span className={style.manual__summaryLabel}>{getString('institution-number-label')}:</span>
							<span className={style.manual__summaryValue}>{maskValue(manualRouting, 3)}</span>
						</li>
					)}
					<li className={style.manual__summaryItem}>
						<span className={style.manual__summaryLabel}>{getString('account-number-label')}:</span>
						<span className={style.manual__summaryValue}>
							{retainInfoSource === 'saved' ? savedAccountDisplay : maskValue(manualAccount, 4)}
						</span>
					</li>
				</ul>
			</div>

			<div className={style.manual__confirmation}>
				<Confirm
					titleLabel={getString('use-check-info-prompt')}
					yesButton={{ label: getString('yes'), value: 'yes', target: '' }}
					noButton={{ label: getString('no'), value: 'no', target: '' }}
					yesCallback={handleManualPaymentConfirm}
					noCallback={handleConfirmationDeny}
				/>
			</div>
		</section>
	);

	const renderErrorView = () => (
		<section className={style.error}>
			<MainMessageBox icon={AppIconType.Error} message={error?.message ?? getString('checkErrorGeneric')} />
			<Confirm
				titleLabel={getString('try-again-message')}
				yesButton={{ label: getString('yes'), value: 'yes', target: '' }}
				noButton={{ label: getString('no'), value: 'no', target: '' }}
				yesCallback={handleRetry}
				noCallback={handleCancel}
			/>
		</section>
	);

	const renderServiceFeeView = () => {
		logger.logGeneral.log({}, 'Rendering service fee view');
		return (
			<ServiceFeeView
				amount={cartTotalAmount}
				fee={parseAmountToNumber(transactionFee!)}
				onConfirm={handleServiceFeeConfirm}
				onCancel={handleServiceFeeCancel}
				labels={
					paymentFeeViewServerProps
						? paymentFeeViewServerProps
						: ({ titleLabelKey: 'service-fee-description-check' } as PaymentFeeViewServerProps)
				}
			/>
		);
	};

	const renderGetCheckView = () => <GetCheckView doneCallback={() => setPaymentState(PaymentCheckState.DONE)} />;

	const renderCurrentView = () => {
		switch (paymentState) {
			case PaymentCheckState.SERVICE_FEE:
				return renderServiceFeeView();
			case PaymentCheckState.OPTIONS:
				return renderOptionsView();
			case PaymentCheckState.AWAITING_INSERT:
				return renderPayView();
			case PaymentCheckState.MANUAL_ENTRY:
				return renderManualEntryView();
			case PaymentCheckState.RETAIN_INFO:
				return renderRetainInfoView();
			case PaymentCheckState.ERROR:
				return renderErrorView();
			case PaymentCheckState.AWAITING_REMOVAL:
				return renderGetCheckView();
			default:
				return null;
		}
	};

	return <section className={style.section}>{renderCurrentView()}</section>;
};

export default PaymentCheckWithManualEntry;
